import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import { Stack, Typography, Divider, useTheme, Fade } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, useState } from "react"
import { useDispatch } from "react-redux"
import { useSearchParams } from "react-router-dom"

import SelectedFilterTab from "./selectedFilterTab"
import {
    DRAFT_ID,
    DRAFT_UPDATED_DATE_FROM,
    DRAFT_UPDATED_DATE_TO,
    FILTER_PANEL_SLIDE_MS,
    POSTCODE,
    STATUS_FROM,
    STATUS_TO,
    TECHNOLOGY,
} from "../../constants/generalConstants"
import { useDraftsApplicationFilters } from "../../hooks/useDraftsApplicationFilters"
import { useGetState } from "../../hooks/useGetState"
import { useScreenSize } from "../../hooks/useScreenSize"
import {
    getShowSearchFilters,
    setShowSearchFilters,
} from "../../redux/preferencesSlice"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import AppButton from "../buttons/appButton"
import HelpIcon from "../helper/helpIcon"
import RevalidatingSpinner from "../loading/spinners/revalidatingSpinner"
import AppPaper from "../surfaces/appPaper"
import DisplayIf from "../wrappers/displayIf"
import DisplayIfElse from "../wrappers/displayIfElse"

interface IFilterTabBarFrameProps {
    draftId: string
    isValidating: boolean
    tooltip: string
    drafts: string
    clearLocalDraftId: () => void
    clearLocalPostcode: () => void
}

const FilterFocusIcon = ({
    showFilters,
}: {
    showFilters: boolean
}): ReactElement => (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)

/**
 * Abstracts layout for the panel tabs showing selected filters for
 * list draft pages in web portal
 */
const DraftsFilterPanelFrame = ({
    draftId,
    isValidating,
    tooltip,
    drafts,
    clearLocalDraftId,
    clearLocalPostcode,
}: IFilterTabBarFrameProps): ReactElement => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const showFilters = useGetState(getShowSearchFilters)
    const [filterButtonFocus, setFilterButtonFocus] = useState(false)
    const [searchParams, setSearchParams] = useSearchParams()
    const { isTabletOrMob } = useScreenSize()
    // Filter and sort hooks
    const {
        setFilterFromTextfield,
        clearAllFilters,
        dateFrom,
        dateTo,
        status,
        technology,
        postcode,
    } = useDraftsApplicationFilters(searchParams, setSearchParams)

    /**
     * Clears all local state and URL search params
     */
    const clearAllLocalAndUrl = (): void => {
        clearAllFilters()
        clearLocalDraftId()
        clearLocalPostcode()
    }

    return (
        <Grid xs={12}>
            <Stack
                direction="row"
                spacing={GRID_SPACING}
                alignItems="center"
                justifyContent="space-between"
            >
                <DisplayIfElse
                    condition={isTabletOrMob}
                    displayIfTrue={
                        <Typography color="text.primary" variant="h6">
                            My Drafts
                        </Typography>
                    }
                    displayIfFalse={
                        <AppPaper
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                paddingY: theme.spacing(1),
                            }}
                        >
                            <Typography variant="h6" component="div">
                                {drafts}
                            </Typography>
                            <HelpIcon tooltip={tooltip} />
                        </AppPaper>
                    }
                />

                <Stack
                    direction="row"
                    spacing={GRID_SPACING}
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <RevalidatingSpinner isValidating={isValidating} />
                    <DisplayIf condition={!isTabletOrMob}>
                        <>
                            <Stack
                                direction="row"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={2}
                            >
                                <SelectedFilterTab
                                    name="Draft ID"
                                    value={draftId}
                                    onDelete={() => {
                                        setFilterFromTextfield("", DRAFT_ID)
                                        clearLocalDraftId()
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Date From"
                                    value={dateFrom}
                                    onDelete={() => {
                                        setFilterFromTextfield(
                                            "",
                                            DRAFT_UPDATED_DATE_FROM
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Date To"
                                    value={dateTo}
                                    onDelete={() => {
                                        setFilterFromTextfield(
                                            "",
                                            DRAFT_UPDATED_DATE_TO
                                        )
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Completion Status"
                                    value={status}
                                    onDelete={() => {
                                        setFilterFromTextfield("", STATUS_FROM)
                                        setFilterFromTextfield("", STATUS_TO)
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Technology"
                                    value={technology}
                                    onDelete={() => {
                                        setFilterFromTextfield("", TECHNOLOGY)
                                    }}
                                />
                                <SelectedFilterTab
                                    name="Postcode"
                                    value={postcode}
                                    onDelete={() => {
                                        clearLocalPostcode()
                                        setFilterFromTextfield("", POSTCODE)
                                    }}
                                />
                                <Fade
                                    in={
                                        !!draftId ||
                                        !!dateFrom ||
                                        !!dateTo ||
                                        !!status ||
                                        !!technology ||
                                        !!postcode
                                    }
                                    unmountOnExit
                                    timeout={FILTER_PANEL_SLIDE_MS}
                                >
                                    <AppButton
                                        variant="outlined"
                                        onClick={clearAllLocalAndUrl}
                                        endIcon={<ClearOutlinedIcon />}
                                    >
                                        Clear All
                                    </AppButton>
                                </Fade>
                            </Stack>
                            <Divider orientation="vertical" flexItem />
                        </>
                    </DisplayIf>
                    <AppButton
                        onClick={() => dispatch(setShowSearchFilters())}
                        endIcon={
                            filterButtonFocus ? (
                                <FilterFocusIcon showFilters={showFilters} />
                            ) : (
                                <FilterAltIcon />
                            )
                        }
                        color="secondary"
                        onMouseEnter={() => setFilterButtonFocus(true)}
                        onMouseLeave={() => setFilterButtonFocus(false)}
                    >
                        Filters
                    </AppButton>
                </Stack>
            </Stack>
        </Grid>
    )
}

export default DraftsFilterPanelFrame
